import { useState, useEffect, useRef, lazy, Suspense } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { Helmet } from "react-helmet-async";
import { type Product, type Category } from "@shared/schema";
import { ProductCard } from "@/components/product-card";
// The order form (with its validation libraries) is loaded only when the visitor approaches it.
const loadOrderForm = () => import("@/components/order-form");
const OrderForm = lazy(() => loadOrderForm().then((m) => ({ default: m.OrderForm })));
import { TotalCalculator } from "@/components/total-calculator";
import { Calendar, Truck } from "lucide-react";
import { StatusMark } from "@/components/status-mark";
import { Card } from "@/components/ui/card";
import { apiRequest } from "@/lib/queryClient";
import { registerWebMcpTools, toolResult } from "@/lib/webmcp";
import { cardImageSrcSet, CARD_IMAGE_SIZES, CARD_IMAGE_WIDTHS, THUMB_WIDTH, imageUrl } from "@shared/imageSizes";

// Generate or retrieve session ID
const getSessionId = () => {
  let sessionId = localStorage.getItem("analytics_session_id");
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem("analytics_session_id", sessionId);
  }
  return sessionId;
};

const VISIBLE_PER_CATEGORY = 3;

interface AppSettings {
  orderDate: string;
  deliveryDate: string;
}

export default function Home() {
  const [quantities, setQuantities] = useState<Record<number, number>>({});
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [sessionId] = useState(getSessionId);
  const [expandedCategories, setExpandedCategories] = useState<Set<number>>(new Set());

  // Fetch products from API
  const { data: products = [], isLoading: isLoadingProducts, isError, error } = useQuery<Product[]>({
    queryKey: ["/api/products"],
  });

  // Fetch categories from API
  const { data: categories = [], isLoading: isLoadingCategories } = useQuery<Category[]>({
    queryKey: ["/api/categories"],
  });

  // Fetch settings from API
  const { data: settings } = useQuery<AppSettings>({
    queryKey: ["/api/settings"],
  });

  // Order form: its code is downloaded in the background shortly after the page has loaded, and the
  // form is rendered as soon as the last product category comes into view, so the fields are
  // already there when the visitor scrolls down to the form.
  const orderSectionRef = useRef<HTMLElement>(null);
  const lastCategoryRef = useRef<HTMLElement | null>(null);
  const [orderFormNear, setOrderFormNear] = useState(false);
  useEffect(() => {
    let timer: number | undefined;
    const prefetch = () => { timer = window.setTimeout(() => { loadOrderForm().catch(() => {}); }, 1500); };
    if (document.readyState === "complete") prefetch();
    else window.addEventListener("load", prefetch, { once: true });
    return () => {
      window.removeEventListener("load", prefetch);
      window.clearTimeout(timer);
    };
  }, []);
  useEffect(() => {
    if (orderFormNear) return;
    const targets = [lastCategoryRef.current, orderSectionRef.current].filter((el): el is HTMLElement => el !== null);
    if (targets.length === 0) return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        setOrderFormNear(true);
        io.disconnect();
      }
    }, { rootMargin: "600px 0px" });
    targets.forEach((el) => io.observe(el));
    return () => io.disconnect();
  });

  // Combined loading state
  const isLoading = isLoadingProducts || isLoadingCategories;

  // Preload the images of the hidden cards when a "Alle … anzeigen" button comes near the screen,
  // so the cards show their images immediately when opened. Same srcset/sizes as the card, so the
  // browser picks the same file and reuses it from the cache.
  const preloadedImagesRef = useRef<Map<number, HTMLImageElement[]>>(new Map());
  useEffect(() => {
    if (isLoading) return;
    const preload = (categoryId: number) => {
      if (preloadedImagesRef.current.has(categoryId)) return;
      const hidden = products
        .filter(p => p.categoryId === categoryId && p.variantOf == null)
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .slice(VISIBLE_PER_CATEGORY);
      const images = hidden
        .filter(p => p.images[0])
        .map(p => {
          const img = new Image();
          img.decoding = "async";
          img.sizes = CARD_IMAGE_SIZES;
          const srcSet = cardImageSrcSet(p.images[0]);
          if (srcSet) img.srcset = srcSet;
          img.src = imageUrl(p.images[0], CARD_IMAGE_WIDTHS[1]);
          img.decode().catch(() => {});
          return img;
        });
      // The small thumbnails below the image are visible right away too.
      for (const p of hidden) {
        if (p.images.length < 2) continue;
        for (const src of p.images) {
          const thumb = new Image();
          thumb.src = imageUrl(src, THUMB_WIDTH);
          images.push(thumb);
        }
      }
      preloadedImagesRef.current.set(categoryId, images);
    };
    const io = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        preload(Number((entry.target as HTMLElement).dataset.categoryId));
        io.unobserve(entry.target);
      }
    }, { rootMargin: "800px 0px" });
    // Start only after the page has finished loading, so it never competes with the visible images.
    const start = () => document.querySelectorAll<HTMLElement>(".category-more[data-category-id]").forEach(el => io.observe(el));
    let timer: number | undefined;
    const onLoad = () => { timer = window.setTimeout(start, 300); };
    if (document.readyState === "complete") onLoad();
    else window.addEventListener("load", onLoad, { once: true });
    return () => {
      window.removeEventListener("load", onLoad);
      window.clearTimeout(timer);
      io.disconnect();
    };
  }, [isLoading, products]);

  // WebMCP: expose the shop's key actions to AI agents in the browser.
  // Tools read the latest state through a ref, so they are registered only once.
  const shopStateRef = useRef({ products, categories, settings, quantities });
  shopStateRef.current = { products, categories, settings, quantities };
  useEffect(() => {
    if (isLoading) return;
    const state = () => shopStateRef.current;
    const price = (p: Product) => parseFloat(p.price);
    const categoryName = (id: number | null) => state().categories.find((c) => c.id === id)?.name ?? null;
    const cartSummary = () => {
      const { products, quantities } = state();
      const items = Object.entries(quantities)
        .filter(([, q]) => q > 0)
        .map(([id, quantity]) => {
          const p = products.find((x) => x.id === Number(id));
          return p ? { productId: p.id, name: p.name, quantity, unitPrice: price(p), subtotal: +(price(p) * quantity).toFixed(2) } : null;
        })
        .filter((x): x is NonNullable<typeof x> => x !== null);
      return { items, total: +items.reduce((sum, i) => sum + i.subtotal, 0).toFixed(2), currency: "CHF" };
    };

    const controller = registerWebMcpTools([
      {
        name: "get_order_round",
        title: "Aktuelle Sammelbestellung",
        description: "Order deadline and planned delivery date of the current group order.",
        inputSchema: { type: "object", properties: {} },
        annotations: { readOnlyHint: true },
        execute: () => toolResult({
          orderDate: state().settings?.orderDate ?? null,
          deliveryDate: state().settings?.deliveryDate ?? null,
          shipping: "Die Lieferanten organisieren den Versand in die Schweiz innert 7 Arbeitstagen.",
        }),
      },
      {
        name: "search_products",
        title: "Produkte suchen",
        description: "List products with id, category, description and price in CHF. Optionally filter by category name or search term. Sizes are separate products (use their id to add them to the cart).",
        inputSchema: {
          type: "object",
          properties: {
            category: { type: "string", description: "Category name, e.g. \"Tee\" (case-insensitive)" },
            query: { type: "string", description: "Search term matched against name and description" },
          },
        },
        annotations: { readOnlyHint: true },
        execute: (input) => {
          const category = typeof input.category === "string" ? input.category.toLowerCase() : "";
          const query = typeof input.query === "string" ? input.query.toLowerCase() : "";
          const list = state().products
            .filter((p) => !category || categoryName(p.categoryId)?.toLowerCase() === category)
            .filter((p) => !query || p.name.toLowerCase().includes(query) || p.description?.toLowerCase().includes(query))
            .map((p) => ({
              id: p.id,
              name: p.name,
              size: p.variantLabel ?? undefined,
              category: categoryName(p.categoryId),
              price: price(p),
              currency: "CHF",
              description: p.description,
            }));
          return toolResult({ count: list.length, products: list });
        },
      },
      {
        name: "set_cart_quantity",
        title: "Menge im Warenkorb setzen",
        description: "Set how many units of a product are in the order (0 removes it). Use ids from search_products. The person still fills in the order form and submits it themselves.",
        inputSchema: {
          type: "object",
          properties: {
            productId: { type: "integer", description: "Product id from search_products" },
            quantity: { type: "integer", minimum: 0, maximum: 99, description: "New quantity" },
          },
          required: ["productId", "quantity"],
        },
        execute: (input) => {
          const productId = Number(input.productId);
          const quantity = Math.max(0, Math.min(99, Math.floor(Number(input.quantity))));
          if (!state().products.some((p) => p.id === productId) || Number.isNaN(quantity)) {
            return toolResult({ error: "Unknown productId or invalid quantity" });
          }
          setQuantities((prev) => ({ ...prev, [productId]: quantity }));
          state().quantities = { ...state().quantities, [productId]: quantity };
          return toolResult(cartSummary());
        },
      },
      {
        name: "get_cart",
        title: "Warenkorb anzeigen",
        description: "Current order: items, quantities and total in CHF.",
        inputSchema: { type: "object", properties: {} },
        annotations: { readOnlyHint: true },
        execute: () => toolResult(cartSummary()),
      },
      {
        name: "open_order_form",
        title: "Zum Bestellformular",
        description: "Scroll to the order form, where the person enters name, address and e-mail and submits the order.",
        inputSchema: { type: "object", properties: {} },
        execute: () => {
          document.getElementById("order-form")?.scrollIntoView({ behavior: "smooth" });
          return toolResult({ ok: true, cart: cartSummary() });
        },
      },
    ]);
    return () => controller.abort();
  }, [isLoading]);

  // Only show the loading notice if loading takes noticeably long, so fast loads don't flash it.
  const [showLoader, setShowLoader] = useState(false);
  useEffect(() => {
    if (!isLoading) { setShowLoader(false); return; }
    const timer = setTimeout(() => setShowLoader(true), 1000);
    return () => clearTimeout(timer);
  }, [isLoading]);

  // Sort categories by sortOrder and filter only those with products
  const sortedCategories = categories
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .filter(category => products.some(p => p.categoryId === category.id));

  // Scroll to top when order is successful
  useEffect(() => {
    if (orderSuccess) {
      window.scrollTo({ top: 0, behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? 'auto' : 'smooth' });
    }
  }, [orderSuccess]);

  // Track session on mount
  useEffect(() => {
    const trackSession = async () => {
      try {
        await apiRequest("POST", "/api/analytics/session", { sessionId });
      } catch (error) {
        console.error("Failed to track session:", error);
      }
    };
    
    trackSession();
  }, [sessionId]);

  // Track cart abandonment when items change
  useEffect(() => {
    const selectedItems = getSelectedItems();
    const totalAmount = calculateTotal();

    if (selectedItems.length > 0 && totalAmount > 0) {
      const trackAbandonment = async () => {
        try {
          await apiRequest("POST", "/api/analytics/cart-abandonment", {
            sessionId,
            items: selectedItems,
            totalAmount: totalAmount.toFixed(2),
          });
        } catch (error) {
          console.error("Failed to track cart abandonment:", error);
        }
      };

      // Debounce to avoid too many requests
      const timeoutId = setTimeout(trackAbandonment, 1000);
      return () => clearTimeout(timeoutId);
    }
  }, [quantities, sessionId]);

  const handleShare = async () => {
    const shareData = {
      title: "Verein Dalia – Sammelbestellung",
      text: "Schau dir die Sammelbestellung von Verein Dalia an: Produkte aus Israel, direkt bestellt.",
      url: "https://vereindalia.ch",
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch {
        // Share sheet closed by the user
      }
      return;
    }
    try {
      await navigator.clipboard.writeText(shareData.url);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2500);
    } catch {
      window.location.href = `mailto:?subject=${encodeURIComponent(shareData.title)}&body=${encodeURIComponent(`${shareData.text}\n${shareData.url}`)}`;
    }
  };

  const updateQuantity = (productId: number, quantity: number) => {
    setQuantities(prev => ({
      ...prev,
      [productId]: quantity
    }));
  };

  const calculateTotal = () => {
    return Object.entries(quantities).reduce((total, [productId, quantity]) => {
      const product = products.find(p => p.id === parseInt(productId));
      if (product && quantity > 0) {
        return total + (parseFloat(product.price) * quantity);
      }
      return total;
    }, 0);
  };

  const getSelectedItems = () => {
    return Object.entries(quantities)
      .filter(([_, quantity]) => quantity > 0)
      .map(([productId, quantity]) => ({
        productId: parseInt(productId),
        quantity
      }));
  };

  // Loading state
  if (isLoading) {
    if (!showLoader) return <main className="min-h-[100dvh] bg-background" aria-busy="true" />;
    return (
      <main className="min-h-[100dvh] bg-background flex items-center justify-center">
        <div className="text-center w-full max-w-lg px-8" role="status">
          <div className="soft-card h-48 mb-8 animate-pulse" aria-hidden="true" />
          <p className="text-lg text-muted-foreground">Produkte werden geladen...</p>
        </div>
      </main>
    );
  }

  // Error state
  if (isError) {
    return (
      <main className="min-h-[100dvh] bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 text-center">
          <h2 className="text-2xl font-serif font-semibold text-foreground mb-4">
            Fehler beim Laden
          </h2>
          <p className="text-muted-foreground mb-6">
            {error instanceof Error ? error.message : "Die Produkte konnten nicht geladen werden."}
          </p>
          <p className="text-sm text-muted-foreground">
            Bitte versuche es später erneut oder kontaktiere uns.
          </p>
        </Card>
      </main>
    );
  }

  if (orderSuccess) {
    return (
      <main className="min-h-[100dvh] bg-background flex items-center justify-center p-5">
        <Card className="max-w-2xl w-full p-8 md:p-12 text-center">
          <div className="mb-6 flex justify-center">
            <div className="success-mark flex items-center justify-center">
              <StatusMark state="done" />
            </div>
          </div>
          <h1 className="text-3xl md:text-4xl font-serif font-semibold text-foreground mb-4">
            Bestellung erfolgreich!
          </h1>
          <p className="text-lg text-muted-foreground mb-6">
            Vielen Dank für deine Bestellung. Du erhältst in Kürze eine Bestätigung per E-Mail mit allen Details und den Zahlungsinformationen.
          </p>
          <p className="text-muted-foreground">
            Bitte überprüfe deinen Posteingang (auch den Spam-Ordner).
          </p>
          <div className="success-actions">
            <button
              type="button"
              className="soft-button soft-button-primary success-action"
              onClick={() => {
                setQuantities({});
                setOrderSuccess(false);
                window.scrollTo({ top: 0 });
              }}
              data-testid="button-back-to-shop"
            >
              Zurück zum Shop
            </button>
            <button
              type="button"
              className="soft-button success-action"
              onClick={handleShare}
              data-testid="button-share"
            >
              {shareCopied ? "Link kopiert" : "Weiterempfehlen"}
            </button>
          </div>
          <p className="sr-only" aria-live="polite">{shareCopied ? "Link in die Zwischenablage kopiert" : ""}</p>
        </Card>
      </main>
    );
  }

  return (
    <div className="site-shell bg-background">
      <Helmet>
        <title>Verein Dalia - Sammelbestellung {settings?.orderDate || "10. April 2026"}</title>
        <meta name="description" content={`Bestelle Alltagsprodukte: koscher zertifizierte Tee-Kollektionen, Ungefiltertes Olivenöl aus entsteinten Oliven, Essential Geschenksets und mehr. Lieferung bis ${settings?.deliveryDate || "10. Mai 2026"}.`} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://vereindalia.ch" />
        <meta property="og:title" content={`Verein Dalia - Sammelbestellung ${settings?.orderDate || "10. April 2026"}`} />
        <meta property="og:description" content={`Bestelle Alltagsprodukte: koscher zertifizierte Tee-Kollektionen, Ungefiltertes Olivenöl aus entsteinten Oliven, Essential Geschenksets und mehr. Lieferung bis ${settings?.deliveryDate || "10. Mai 2026"}.`} />
        <meta property="og:site_name" content="Verein Dalia" />
        <meta property="og:image" content="https://vereindalia.ch/og-image.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Verein Dalia – Israelische Produkte im Alltag" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`Verein Dalia - Sammelbestellung ${settings?.orderDate || "10. April 2026"}`} />
        <meta name="twitter:description" content={`Bestelle Alltagsprodukte: koscher zertifizierte Tee-Kollektionen, Ungefiltertes Olivenöl aus entsteinten Oliven, Essential Geschenksets und mehr. Lieferung bis ${settings?.deliveryDate || "10. Mai 2026"}.`} />
        <meta name="twitter:image" content="https://vereindalia.ch/og-image.jpg" />
      </Helmet>
      {/* Hero Section */}
      <header className="site-hero">
        <div className="site-container">
          <div className="hero-layout">
            <div>
            <div className="brand-lockup">
              <span className="brand-hex" aria-hidden="true">
                <svg viewBox="0 0 60 62" className="brand-hex-shape">
                  <path transform="translate(-4 0)" d="M30 3.5c2.4-1.4 5.6-1.4 8 0l18 10.4c2.4 1.4 4 4 4 6.9v20.8c0 2.9-1.6 5.5-4 6.9L38 58.9c-2.4 1.4-5.6 1.4-8 0L12 48.5c-2.4-1.4-4-4-4-6.9V20.8c0-2.9 1.6-5.5 4-6.9z" />
                </svg>
                <img src="/brand/olivenzweig-logo.svg" alt="" className="brand-hex-icon" />
              </span>
              <div className="brand-text">
                <h1 className="brand-name">Verein Dalia</h1>
                <p className="brand-sub">Israelische Produkte im Alltag</p>
              </div>
            </div>
            </div>
            <div className="hero-date-card">
            <h2 className="hero-date-title">
              Sammelbestellung <em>{settings?.orderDate || "10. Februar 2026"}</em>
            </h2>
            <div className="hero-delivery">
              <div className="flex flex-col gap-3 text-left">
                <div className="flex items-start gap-3">
                  <Truck className="w-6 h-6 flex-shrink-0" />
                  <p className="leading-6">Die Lieferanten organisieren den Versand in die Schweiz innert 7 Arbeitstagen.</p>
                </div>
                <div className="flex items-start gap-3">
                  <Calendar className="w-6 h-6 flex-shrink-0" />
                  <p className="font-semibold leading-6">Wir planen die Bestellungen in der Schweiz um den {settings?.deliveryDate || "10. März 2026"} zu versenden.</p>
                </div>
              </div>
            </div>
            </div>
          </div>
        </div>
      </header>
      {/* Products Section */}
      <main className="site-container shop-content">
        {/* Dynamic Category Sections */}
        {sortedCategories.map((category, categoryIndex) => {
          const categoryProducts = products
            .filter(p => p.categoryId === category.id && p.variantOf == null)
            .sort((a, b) => a.sortOrder - b.sortOrder);

          if (categoryProducts.length === 0) return null;
          const expanded = expandedCategories.has(category.id);

          return (
            <section
              key={category.id}
              ref={categoryIndex === sortedCategories.length - 1 ? lastCategoryRef : undefined}
              className="category-section"
              data-testid={`section-category-${category.id}`}
            >
              <h3 className="category-heading">
                <svg className="kicker-hex" viewBox="4 2 52 58" aria-hidden="true">
                  <path transform="translate(-4 0)" d="M30 3.5c2.4-1.4 5.6-1.4 8 0l18 10.4c2.4 1.4 4 4 4 6.9v20.8c0 2.9-1.6 5.5-4 6.9L38 58.9c-2.4 1.4-5.6 1.4-8 0L12 48.5c-2.4-1.4-4-4-4-6.9V20.8c0-2.9 1.6-5.5 4-6.9z" />
                </svg>
                {category.name}
              </h3>
              <div className="product-grid" id={`category-grid-${category.id}`}>
                {(expanded ? categoryProducts : categoryProducts.slice(0, VISIBLE_PER_CATEGORY)).map((product, index) => (
                  <div
                    key={product.id}
                    className={index >= VISIBLE_PER_CATEGORY ? "product-reveal" : undefined}
                    style={index >= VISIBLE_PER_CATEGORY ? { animationDelay: `${Math.min(index - VISIBLE_PER_CATEGORY, 5) * 70}ms` } : undefined}
                  >
                    <ProductCard
                      product={product}
                      variants={products
                        .filter(p => p.id === product.id || p.variantOf === product.id)
                        .sort((a, b) => parseFloat(a.price) - parseFloat(b.price))}
                      quantities={quantities}
                      onQuantityChange={updateQuantity}
                      priority={categoryIndex === 0 && index < VISIBLE_PER_CATEGORY}
                    />
                  </div>
                ))}
              </div>
              {!expanded && categoryProducts.length > VISIBLE_PER_CATEGORY && (
                <div className="category-more" data-category-id={category.id}>
                  <button
                    type="button"
                    className="soft-button category-more-button"
                    aria-expanded={false}
                    aria-controls={`category-grid-${category.id}`}
                    onClick={() => setExpandedCategories(prev => new Set(prev).add(category.id))}
                    data-testid={`button-show-all-${category.id}`}
                  >
                    Alle «{category.name}» anzeigen
                  </button>
                </div>
              )}
            </section>
          );
        })}

        {/* Order Form Section */}
        <section id="order-form" ref={orderSectionRef} className="mt-20 md:mt-32 scroll-mt-8">
          <div className="order-panel">
            <h3 className="order-heading">
              Deine Bestelldaten
            </h3>
            {orderFormNear ? (
              <Suspense fallback={<div className="min-h-[640px]" aria-busy="true" />}>
                <OrderForm
                  selectedItems={getSelectedItems()}
                  totalAmount={calculateTotal()}
                  sessionId={sessionId}
                  onSuccess={() => setOrderSuccess(true)}
                />
              </Suspense>
            ) : (
              <div className="min-h-[640px]" aria-busy="true" />
            )}
          </div>
        </section>
      </main>
      {/* Sticky Total Calculator */}
      <TotalCalculator
        total={calculateTotal()}
        itemCount={getSelectedItems().length}
      />
      {/* Footer */}
      <footer className="site-footer">
        <div className="site-container">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-muted-foreground">
            <p>&copy; 2025 - {new Date().getFullYear()} Verein Dalia</p>
            <div className="flex gap-6">
              <Link 
                href="/impressum" 
                className="hover:text-foreground transition-colors" 
                data-testid="link-impressum"
              >
                Impressum
              </Link>
              <Link 
                href="/datenschutz" 
                className="hover:text-foreground transition-colors" 
                data-testid="link-datenschutz"
              >
                Datenschutz
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
